import React, { useRef } from 'react';
import { Box, Typography, Button, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { NavigateBefore, NavigateNext, Delete, ChevronRight, Print, Star, StarBorder } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import ReactToPrint from 'react-to-print';
import { useHistory } from 'react-router';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { CommentBox, PostCommentType } from '../../../../common/comment/CommentBox';
import { CommentViewer } from '../../../../common/comment/CommentViewer';
import { useSaveComment } from '../../../../common/comment/Comment';
import { IComment } from '../../../../common/comment/types';
import {
  FormSubmissionListItem,
  FormSubmissionDetail,
  FormFieldConfig,
  SortOrder,
  FormSubmissionFieldValue,
} from './form-responses-types';
import {
  formatSubmissionDate,
  parseUserAgent,
  sortFieldValuesByFormOrder,
  groupFieldValuesByInstance,
} from './form-responses-utils';
import { SortPopover } from './SortPopover';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
    },
    emptyState: {
      backgroundColor: theme.color.grey.neutralBrand100,
      borderRadius: '8px',
      padding: '48px',
      textAlign: 'center',
    },
    emptyStateTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      marginBottom: '8px',
    },
    emptyStateText: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
      maxWidth: '400px',
      margin: '0 auto',
    },
    toolbarButton: {
      backgroundColor: 'transparent',
      color: '#646464',
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      padding: '4.5px 12px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'none',
      minWidth: 'auto',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    responseCard: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '16px',
    },
    responseHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
    },
    responseNav: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    fieldRow: {
      padding: '12px 0',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand100}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    fieldLabel: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    starIcon: {
      cursor: 'pointer',
      fontSize: '18px',
      transition: 'opacity 0.2s ease, color 0.2s ease',
    },
    starIconInactive: {
      opacity: 0.15,
      color: theme.color.grey.neutral300,
      '&:hover': {
        opacity: 0.5,
      },
    },
    starIconActive: {
      opacity: 1,
      color: '#FFB400',
      '&:hover': {
        opacity: 0.7,
      },
    },
    starredDivider: {
      fontSize: '0.7rem',
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      padding: '8px 0 4px',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      marginBottom: '4px',
    },
    fieldValue: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral400,
    },
    metaInfo: {
      fontSize: '0.75rem',
      color: theme.color.grey.neutral300,
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: `1px solid ${theme.color.grey.neutralBrand200}`,
    },
    stickyNav: {
      position: 'fixed',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%) translateX(calc(100% - 24px))',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: 100,
      backgroundColor: 'white',
      borderRadius: '8px 0 0 8px',
      padding: '8px',
      boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
      transition: 'transform 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-50%) translateX(0)',
      },
    },
    stickyNavLabel: {
      writingMode: 'vertical-rl',
      textOrientation: 'mixed',
      transform: 'rotate(180deg)',
      fontSize: '10px',
      fontWeight: 600,
      color: '#646464',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      marginRight: '8px',
    },
    stickyNavButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    stickyNavButton: {
      minWidth: 'auto',
      width: '36px',
      height: '36px',
      padding: '6px',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      color: '#646464',
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand100,
        '& svg path': {
          fill: theme.palette.primary.main,
        },
      },
    },
  })
);

interface IndividualTabProps {
  submissions: FormSubmissionListItem[];
  selectedSubmission: FormSubmissionDetail | null;
  formFields: FormFieldConfig[];
  fieldConfigMap: Map<string, FormFieldConfig>;
  submissionCount: number;
  totalSubmissions: number;
  currentResponseNumber: number;
  hasPrevious: boolean;
  hasNext: boolean;
  listLoading: boolean;
  submissionLoading: boolean;
  sortOrder: SortOrder;
  printRef: React.RefObject<HTMLDivElement>;
  starredFieldIds: string[];
  onToggleStar: (taskFieldId: string) => void;
  onSortChange: (order: SortOrder) => void;
  onPrevResponse: () => void;
  onNextResponse: () => void;
  onOpenWizard: (submissionId: string) => void;
  onDeleteSubmission: (submissionId: string) => void;
  onRefetchSubmission: () => void;
}

export const IndividualTab: React.FC<IndividualTabProps> = ({
  selectedSubmission,
  formFields,
  fieldConfigMap,
  submissionCount,
  totalSubmissions,
  currentResponseNumber,
  hasPrevious,
  hasNext,
  listLoading,
  submissionLoading,
  sortOrder,
  printRef,
  starredFieldIds,
  onToggleStar,
  onSortChange,
  onPrevResponse,
  onNextResponse,
  onOpenWizard,
  onDeleteSubmission,
  onRefetchSubmission,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const history = useHistory();
  const saveComment = useSaveComment();
  const responseTopRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const createdEntitiesRef = useRef<HTMLDivElement>(null);

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography className={classes.sectionTitle}>Individual Responses</Typography>
        <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
          <SortPopover sortOrder={sortOrder} onSortChange={onSortChange} />
          {selectedSubmission && (
            <>
              <Button
                startIcon={
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M7.5 5.6L10 7L8.6 4.5L10 2L7.5 3.4L5 2L6.4 4.5L5 7L7.5 5.6ZM19.5 15.4L17 14L18.4 16.5L17 19L19.5 17.6L22 19L20.6 16.5L22 14L19.5 15.4ZM22 2L19.5 3.4L17 2L18.4 4.5L17 7L19.5 5.6L22 7L20.6 4.5L22 2ZM14.37 7.29C13.98 6.9 13.35 6.9 12.96 7.29L1.29 18.96C0.9 19.35 0.9 19.98 1.29 20.37L3.63 22.71C4.02 23.1 4.65 23.1 5.04 22.71L16.71 11.04C17.1 10.65 17.1 10.02 16.71 9.63L14.37 7.29Z'
                      fill='currentColor'
                    />
                  </svg>
                }
                className={classes.toolbarButton}
                onClick={() => {
                  onOpenWizard(selectedSubmission.formSubmissionId);
                }}
              >
                Form Wizard
              </Button>
              <ReactToPrint
                pageStyle='@page { size: auto; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } }'
                trigger={() => (
                  <Button startIcon={<Print style={{ fontSize: '14px' }} />} className={classes.toolbarButton}>
                    Print
                  </Button>
                )}
                content={() => printRef.current}
              />
              <Button
                startIcon={<Delete style={{ fontSize: '14px' }} />}
                className={classes.toolbarButton}
                style={{ color: '#d32f2f', borderColor: '#d32f2f' }}
                onClick={() => onDeleteSubmission(selectedSubmission.formSubmissionId)}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {(listLoading || submissionLoading) && !selectedSubmission ? (
        <CircularProgressOverlay isLoading />
      ) : submissionCount === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>No responses yet</Typography>
          <Typography className={classes.emptyStateText}>
            Share your form to start collecting submissions. Individual responses will appear here.
          </Typography>
        </Box>
      ) : selectedSubmission ? (
        <>
          {/* Sticky mini-nav */}
          <Box className={classes.stickyNav}>
            <Typography className={classes.stickyNavLabel}>Jump</Typography>
            <Box className={classes.stickyNavButtons}>
              <Button
                className={classes.stickyNavButton}
                title='Response'
                onClick={() => responseTopRef.current?.scrollIntoView({ behavior: 'smooth' })}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z'
                    fill='#646464'
                  />
                </svg>
              </Button>
              <Button
                className={classes.stickyNavButton}
                title='Comments'
                onClick={() => commentsRef.current?.scrollIntoView({ behavior: 'smooth' })}
              >
                <svg width='18' height='18' viewBox='0 0 19 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M9.17279 0C14.2387 0 18.3456 3.31835 18.3456 7.41177C18.3456 9.72424 17.0339 11.7889 14.9802 13.1479C15.2472 14.3704 15.8694 16.1836 17.3264 18C14.3921 17.4918 12.5158 16.0089 11.3559 14.6033C10.6547 14.742 9.92649 14.8235 9.17279 14.8235C4.10686 14.8235 0 11.5052 0 7.41177C0 3.31835 4.10686 0 9.17279 0Z'
                    fill='#646464'
                  />
                </svg>
              </Button>
              {selectedSubmission.creations && selectedSubmission.creations.length > 0 && (
                <Button
                  className={classes.stickyNavButton}
                  title='Created Entities'
                  onClick={() => createdEntitiesRef.current?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M7.5 5.6L10 7L8.6 4.5L10 2L7.5 3.4L5 2L6.4 4.5L5 7L7.5 5.6ZM19.5 15.4L17 14L18.4 16.5L17 19L19.5 17.6L22 19L20.6 16.5L22 14L19.5 15.4ZM22 2L19.5 3.4L17 2L18.4 4.5L17 7L19.5 5.6L22 7L20.6 4.5L22 2ZM14.37 7.29C13.98 6.9 13.35 6.9 12.96 7.29L1.29 18.96C0.9 19.35 0.9 19.98 1.29 20.37L3.63 22.71C4.02 23.1 4.65 23.1 5.04 22.71L16.71 11.04C17.1 10.65 17.1 10.02 16.71 9.63L14.37 7.29Z'
                      fill='#646464'
                    />
                  </svg>
                </Button>
              )}
            </Box>
          </Box>

          {/* Response card */}
          <Box ref={responseTopRef} className={classes.responseCard}>
            <Box className={classes.responseHeader}>
              <Box>
                <Typography className={classes.sectionTitle}>
                  Response {currentResponseNumber} of {totalSubmissions}
                </Typography>
                <Typography className={classes.statLabel} style={{ marginTop: '4px' }}>
                  {formatSubmissionDate(selectedSubmission.submissionDate)}
                </Typography>
              </Box>
              <Box className={classes.responseNav}>
                <TabletButton
                  variant='outlined'
                  size='small'
                  disabled={!hasPrevious || listLoading}
                  onClick={onPrevResponse}
                  style={{
                    borderColor: hasPrevious ? theme.color.grey.neutralBrand300 : theme.color.grey.neutral200,
                    color: hasPrevious ? theme.color.grey.neutral400 : theme.color.grey.neutral200,
                    minWidth: '40px',
                    padding: '6px',
                  }}
                >
                  <NavigateBefore style={{ fontSize: '20px' }} />
                </TabletButton>
                <TabletButton
                  variant='outlined'
                  size='small'
                  disabled={!hasNext || listLoading}
                  onClick={onNextResponse}
                  style={{
                    borderColor: hasNext ? theme.color.grey.neutralBrand300 : theme.color.grey.neutral200,
                    color: hasNext ? theme.color.grey.neutral400 : theme.color.grey.neutral200,
                    minWidth: '40px',
                    padding: '6px',
                  }}
                >
                  <NavigateNext style={{ fontSize: '20px' }} />
                </TabletButton>
              </Box>
            </Box>

            <Box>
              {selectedSubmission.formFieldValues && selectedSubmission.formFieldValues.length > 0 ? (
                (() => {
                  const groupFieldIds = new Set<string>();
                  const groupMetadata = new Map<string, { title: string; label: string }>();
                  formFields.forEach((f) => {
                    if (f.groupFormItemId) {
                      groupFieldIds.add(f.taskFieldId);
                      if (!groupMetadata.has(f.groupFormItemId)) {
                        groupMetadata.set(f.groupFormItemId, {
                          title: f.groupTitle || 'Group',
                          label: f.groupLabel || 'item',
                        });
                      }
                    }
                  });

                  const { ungrouped, grouped } = groupFieldValuesByInstance(
                    selectedSubmission.formFieldValues,
                    groupFieldIds
                  );

                  const sorted = sortFieldValuesByFormOrder(ungrouped, formFields);
                  const starred = sorted.filter((fv) => starredFieldIds.includes(fv.taskFieldId));
                  const unstarred = sorted.filter((fv) => !starredFieldIds.includes(fv.taskFieldId));
                  const orderedFields = [...starred, ...unstarred];
                  const hasStarred = starred.length > 0;

                  const groupOrder: string[] = [];
                  const groupsByFormItemId = new Map<
                    string,
                    Array<{ instanceId: string; values: FormSubmissionFieldValue[] }>
                  >();
                  formFields.forEach((f) => {
                    if (f.groupFormItemId && !groupsByFormItemId.has(f.groupFormItemId)) {
                      groupOrder.push(f.groupFormItemId);
                      const instances: Array<{ instanceId: string; values: FormSubmissionFieldValue[] }> = [];
                      grouped.forEach((group) => {
                        const relevantValues = group.values.filter((v) =>
                          formFields.some(
                            (ff) => ff.groupFormItemId === f.groupFormItemId && ff.taskFieldId === v.taskFieldId
                          )
                        );
                        if (relevantValues.length > 0) {
                          instances.push({ instanceId: group.groupInstanceId, values: relevantValues });
                        }
                      });
                      groupsByFormItemId.set(f.groupFormItemId, instances);
                    }
                  });

                  const renderFieldValue = (fieldValue: FormSubmissionFieldValue) => {
                    const isStarred = starredFieldIds.includes(fieldValue.taskFieldId);
                    return (
                      <Box key={fieldValue.formFieldValueId} className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel} component='div'>
                          <span>{fieldValue.fieldName}</span>
                          {isStarred ? (
                            <Star
                              className={`${classes.starIcon} ${classes.starIconActive}`}
                              onClick={() => onToggleStar(fieldValue.taskFieldId)}
                            />
                          ) : (
                            <StarBorder
                              className={`${classes.starIcon} ${classes.starIconInactive}`}
                              onClick={() => onToggleStar(fieldValue.taskFieldId)}
                            />
                          )}
                        </Typography>
                        {fieldValue.fieldType === 'RatingTaskField' ? (
                          <Box display='flex' alignItems='center'>
                            {[1, 2, 3, 4, 5].map((star) =>
                              star <= parseInt(fieldValue.value, 10) ? (
                                <Star key={star} style={{ color: '#FFB400', fontSize: '20px' }} />
                              ) : (
                                <StarBorder key={star} style={{ color: '#FFB400', fontSize: '20px' }} />
                              )
                            )}
                            <Typography
                              style={{ marginLeft: '8px', fontSize: '0.875rem', color: theme.color.grey.neutral300 }}
                            >
                              ({fieldValue.value}/5)
                            </Typography>
                          </Box>
                        ) : fieldValue.fieldType === 'NpsTaskField' ? (
                          <Box display='flex' alignItems='center'>
                            {(() => {
                              const npsValue = parseInt(fieldValue.value, 10);
                              let color = '#4CAF50';
                              let npsLabel = 'Promoter';
                              if (npsValue <= 6) {
                                color = '#F44336';
                                npsLabel = 'Detractor';
                              } else if (npsValue <= 8) {
                                color = '#FF9800';
                                npsLabel = 'Passive';
                              }
                              return (
                                <>
                                  <Box
                                    style={{
                                      backgroundColor: color,
                                      color: '#fff',
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      minWidth: '32px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {npsValue}
                                  </Box>
                                  <Typography
                                    style={{
                                      marginLeft: '8px',
                                      fontSize: '0.75rem',
                                      color: theme.color.grey.neutral300,
                                    }}
                                  >
                                    {npsLabel}
                                  </Typography>
                                </>
                              );
                            })()}
                          </Box>
                        ) : fieldValue.fieldType === 'LinearScaleTaskField' ? (
                          (() => {
                            const config = fieldConfigMap.get(fieldValue.taskFieldId);
                            return (
                              <Box display='flex' alignItems='center'>
                                {config?.minLabel && (
                                  <Typography
                                    style={{
                                      marginRight: '8px',
                                      fontSize: '0.75rem',
                                      color: theme.color.grey.neutral300,
                                    }}
                                  >
                                    {config.minLabel}
                                  </Typography>
                                )}
                                <Box
                                  style={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: '#fff',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {fieldValue.value}
                                </Box>
                                {config?.maxLabel && (
                                  <Typography
                                    style={{
                                      marginLeft: '8px',
                                      fontSize: '0.75rem',
                                      color: theme.color.grey.neutral300,
                                    }}
                                  >
                                    {config.maxLabel}
                                  </Typography>
                                )}
                                {(config?.minValue !== undefined || config?.maxValue !== undefined) && (
                                  <Typography
                                    style={{
                                      marginLeft: '8px',
                                      fontSize: '0.75rem',
                                      color: theme.color.grey.neutral300,
                                    }}
                                  >
                                    (scale: {config?.minValue ?? 1}-{config?.maxValue ?? 10})
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()
                        ) : fieldValue.fieldType === 'YesNoTaskField' ? (
                          <Typography className={classes.fieldValue}>
                            {fieldValue.value === 'true' || fieldValue.value === '1' ? 'Yes' : 'No'}
                          </Typography>
                        ) : (
                          <Typography className={classes.fieldValue}>{fieldValue.value || '-'}</Typography>
                        )}
                      </Box>
                    );
                  };

                  return (
                    <>
                      {orderedFields.map((fieldValue, index) => (
                        <React.Fragment key={fieldValue.formFieldValueId}>
                          {hasStarred && index === starred.length && <Box style={{ height: '12px' }} />}
                          {renderFieldValue(fieldValue)}
                        </React.Fragment>
                      ))}

                      {groupOrder.map((groupFormItemId) => {
                        const meta = groupMetadata.get(groupFormItemId);
                        const instances = groupsByFormItemId.get(groupFormItemId) || [];
                        if (instances.length === 0) return null;

                        return (
                          <Box key={groupFormItemId} style={{ marginTop: '16px' }}>
                            <Box
                              style={{
                                padding: '8px 0',
                                borderBottom: `2px solid ${theme.color.grey.neutralBrand200}`,
                                marginBottom: '4px',
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  color: theme.color.grey.neutral400,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {meta?.title || 'Group'}
                              </Typography>
                            </Box>
                            {instances.map((instance, instIdx) => (
                              <Box
                                key={instance.instanceId}
                                style={{ marginBottom: instIdx < instances.length - 1 ? '12px' : 0 }}
                              >
                                {instances.length > 1 && (
                                  <Typography
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      color: theme.color.grey.neutral300,
                                      padding: '6px 0 2px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.3px',
                                    }}
                                  >
                                    {meta?.label || 'Entry'} {instIdx + 1}
                                  </Typography>
                                )}
                                {sortFieldValuesByFormOrder(instance.values, formFields).map(renderFieldValue)}
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </>
                  );
                })()
              ) : (
                <Typography className={classes.statLabel}>No field values</Typography>
              )}
            </Box>

            <Box className={classes.metaInfo}>
              {(() => {
                const parsed = parseUserAgent(selectedSubmission.userAgent);
                return (
                  <>
                    <Typography
                      style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300, marginBottom: '4px' }}
                    >
                      Browser: {parsed.browser} | OS: {parsed.os} | Device: {parsed.device}
                    </Typography>
                    <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>
                      IP: {selectedSubmission.ipAddress || '-'} | Referrer: {selectedSubmission.referrer || '-'}
                    </Typography>
                  </>
                );
              })()}
            </Box>
          </Box>

          {/* Comments card */}
          <Box ref={commentsRef} className={classes.responseCard}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
              Comments
            </Typography>
            <CommentBox
              onPost={(body: PostCommentType) => {
                saveComment({
                  formSubmissionId: selectedSubmission.formSubmissionId,
                  content: body.content,
                  attachments: body.attachments,
                }).then(() => {
                  onRefetchSubmission();
                });
              }}
            />
            {selectedSubmission.comments && selectedSubmission.comments.length > 0 && (
              <Box mt={3}>
                {selectedSubmission.comments
                  .filter((comment: IComment) => !comment.parentId && !comment.removedDate)
                  .map((comment: IComment) => (
                    <CommentViewer
                      key={comment.commentId}
                      comment={comment}
                      comments={selectedSubmission.comments as IComment[]}
                      onReply={(parentId: string, replyContent: string) => {
                        saveComment({
                          formSubmissionId: selectedSubmission.formSubmissionId,
                          content: replyContent,
                          parentId,
                        }).then(() => {
                          onRefetchSubmission();
                        });
                      }}
                      handleEdit={(body: PostCommentType) => {
                        saveComment({
                          formSubmissionId: selectedSubmission.formSubmissionId,
                          content: body.content,
                          commentId: body.commentId,
                          attachments: body.attachments,
                        }).then(() => {
                          onRefetchSubmission();
                        });
                      }}
                      refetch={onRefetchSubmission}
                    />
                  ))}
              </Box>
            )}
          </Box>

          {/* Created entities card */}
          {selectedSubmission.creations && selectedSubmission.creations.length > 0 && (
            <Box ref={createdEntitiesRef} className={classes.responseCard}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                Created Entities
              </Typography>
              <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
                Entities created from this form submission using the Form Wizard.
              </Typography>
              <Box>
                {selectedSubmission.creations.map((creation) => (
                  <Box
                    key={creation.formSubmissionCreationId}
                    onClick={() => {
                      if (creation.entityType === 'activity') {
                        history.push(`/management/activities?activityId=${creation.entityId}`);
                      } else if (creation.entityType === 'roster_template') {
                        const now = new Date();
                        const today = now.toISOString().split('T')[0];
                        const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
                        const endDate = end.toISOString().split('T')[0];
                        history.push(
                          `/management/rosters?rosterTemplateId=${creation.entityId}&startDate=${today}&endDate=${endDate}&sortVolsRowBy=availability`
                        );
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: theme.color.grey.neutralBrand100,
                      borderRadius: '8px',
                      marginBottom: '8px',
                      cursor:
                        creation.entityType === 'activity' || creation.entityType === 'roster_template'
                          ? 'pointer'
                          : 'default',
                    }}
                  >
                    <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                      <Box
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          backgroundColor: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {creation.entityType === 'activity' ? (
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z'
                              fill='#fff'
                            />
                          </svg>
                        ) : creation.entityType === 'calendar_invite' ? (
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z'
                              fill='#fff'
                            />
                          </svg>
                        ) : creation.entityType === 'email' ? (
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z'
                              fill='#fff'
                            />
                          </svg>
                        ) : (
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M19 3H14.82C14.4 1.84 13.3 1 12 1C10.7 1 9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 3C12.55 3 13 3.45 13 4C13 4.55 12.55 5 12 5C11.45 5 11 4.55 11 4C11 3.45 11.45 3 12 3ZM14 17H7V15H14V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z'
                              fill='#fff'
                            />
                          </svg>
                        )}
                      </Box>
                      <Box>
                        <Typography
                          style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.color.grey.neutral400 }}
                        >
                          {creation.entityType === 'activity'
                            ? 'Activity'
                            : creation.entityType === 'roster_template'
                            ? 'Roster Template'
                            : creation.entityType === 'calendar_invite'
                            ? 'Calendar Invite'
                            : creation.entityType === 'email'
                            ? 'Email'
                            : creation.entityType}
                        </Typography>
                        <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>
                          Created by {creation.createdByProfile?.preferredName || 'Unknown'}{' '}
                          {creation.createdByProfile?.lastName || ''} on {formatSubmissionDate(creation.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    {(creation.entityType === 'activity' || creation.entityType === 'roster_template') && (
                      <ChevronRight style={{ color: theme.color.grey.neutral300 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      ) : listLoading ? (
        <CircularProgressOverlay isLoading />
      ) : null}
    </Box>
  );
};
